import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  CalendarDays,
  Clock,
  MapPin,
  UserRound,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertTriangle,
  CalendarClock,
  ArrowLeft,
  Download,
} from "lucide-react";

import api from "../../services/api";
import { getBackendUrl } from "../../config";

// Public page — reachable without a session, opened by the contact from the
// WhatsApp reminder link. It must never assume an authenticated user.

const formatDate = (iso, timeZone) =>
  new Intl.DateTimeFormat("es-ES", {
    timeZone,
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(iso));

const formatTime = (iso, timeZone) =>
  new Intl.DateTimeFormat("es-ES", {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(iso));

const formatDayHeading = (dateString, timeZone) =>
  new Intl.DateTimeFormat("es-ES", {
    timeZone,
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date(`${dateString}T12:00:00Z`));

const STATUS_VIEW = {
  confirmed: {
    icon: CheckCircle2,
    tone: "text-emerald-600",
    ring: "bg-emerald-50 dark:bg-emerald-950/40",
    title: "¡Cita confirmada!",
    body: "Gracias por confirmar. Le esperamos el día y la hora indicados.",
  },
  cancelled: {
    icon: XCircle,
    tone: "text-red-500",
    ring: "bg-red-50 dark:bg-red-950/40",
    title: "Cita cancelada",
    body: "Su cita quedó cancelada. Si desea reagendar, respóndanos por WhatsApp.",
  },
  completed: {
    icon: CheckCircle2,
    tone: "text-indigo-600",
    ring: "bg-indigo-50 dark:bg-indigo-950/40",
    title: "Cita finalizada",
    body: "Esta cita ya fue atendida.",
  },
  no_show: {
    icon: AlertTriangle,
    tone: "text-amber-500",
    ring: "bg-amber-50 dark:bg-amber-950/40",
    title: "Cita no atendida",
    body: "Esta cita quedó registrada como no asistida.",
  },
};

const ERROR_MESSAGES = {
  ERR_APPOINTMENT_IN_PAST: "Esta cita ya pasó, no es posible modificarla.",
  ERR_APPOINTMENT_ALREADY_CANCELLED: "Esta cita ya había sido cancelada.",
  ERR_APPOINTMENT_SLOT_UNAVAILABLE:
    "Ese horario acaba de ocuparse. Elija otro, por favor.",
  ERR_APPOINTMENT_RESCHEDULE_LIMIT:
    "Ya reprogramó esta cita el máximo de veces permitido. Escríbanos por WhatsApp.",
};

const Row = ({ icon: Icon, children }) => (
  <div className="flex items-start gap-3">
    <Icon className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
    <p className="text-sm text-slate-700 dark:text-slate-300">{children}</p>
  </div>
);

// Google Calendar wants UTC basic-format timestamps: 20260727T130000Z
const toGoogleDate = (date) =>
  `${date.toISOString().replace(/[-:]/g, "").split(".")[0]}Z`;

const buildGoogleUrl = (appointment) => {
  const start = new Date(appointment.scheduledAt);
  const end = new Date(
    start.getTime() + (appointment.durationMinutes || 30) * 60000
  );
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: appointment.reason || "Cita",
    dates: `${toGoogleDate(start)}/${toGoogleDate(end)}`,
  });
  if (appointment.location) params.set("location", appointment.location);
  if (appointment.professional) {
    params.set("details", `Le atiende: ${appointment.professional}`);
  }
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const AppointmentConfirm = () => {
  const { token } = useParams();
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [error, setError] = useState(null);

  const [mode, setMode] = useState("view"); // view | reschedule
  const [slotsData, setSlotsData] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const icsUrl = `${(getBackendUrl() || "").replace(/\/+$/, "")}/public/appointments/${token}/ics`;

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get(`/public/appointments/${token}`);
        setAppointment(data);
      } catch (err) {
        setError(
          err?.response?.status === 404
            ? "Este enlace no es válido o la cita ya no existe."
            : "No pudimos cargar su cita. Intente de nuevo en unos minutos."
        );
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const describeError = (err) =>
    ERROR_MESSAGES[err?.response?.data?.error] ||
    "No pudimos registrar su respuesta. Intente de nuevo.";

  const act = async (action) => {
    setActing(true);
    setError(null);
    try {
      const { data } = await api.post(`/public/appointments/${token}/${action}`);
      setAppointment(data);
      setMode("view");
    } catch (err) {
      setError(describeError(err));
    } finally {
      setActing(false);
    }
  };

  const openReschedule = async () => {
    setMode("reschedule");
    setError(null);
    setLoadingSlots(true);
    try {
      const { data } = await api.get(`/public/appointments/${token}/slots`, {
        params: { days: 21 },
      });
      setSlotsData(data);
    } catch (err) {
      setError(describeError(err));
      setSlotsData({ days: [], hasAvailability: false, canReschedule: false });
    } finally {
      setLoadingSlots(false);
    }
  };

  const pickSlot = async (slotStart) => {
    setActing(true);
    setError(null);
    try {
      const { data } = await api.post(
        `/public/appointments/${token}/reschedule`,
        { scheduledAt: slotStart }
      );
      setAppointment(data);
      setMode("view");
    } catch (err) {
      setError(describeError(err));
      // The offer is stale if the slot was taken — refresh it.
      if (err?.response?.data?.error === "ERR_APPOINTMENT_SLOT_UNAVAILABLE") {
        openReschedule();
      }
    } finally {
      setActing(false);
    }
  };

  const isPast =
    appointment && new Date(appointment.scheduledAt).getTime() < Date.now();
  const isOpen = appointment && !isPast && appointment.status === "scheduled";
  const resolved = appointment && STATUS_VIEW[appointment.status];
  const showCalendarButtons =
    appointment && !isPast && appointment.status === "confirmed";

  const calendarButtons = showCalendarButtons && (
    <div className="px-6 pb-6 space-y-2">
      <p className="text-center text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
        Guardar en mi calendario
      </p>
      <div className="flex gap-2">
        <a
          href={buildGoogleUrl(appointment)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
        >
          <CalendarDays className="w-4 h-4 text-indigo-500" />
          Google
        </a>
        <a
          href={icsUrl}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
        >
          <Download className="w-4 h-4 text-indigo-500" />
          Apple / Outlook
        </a>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-slate-100 dark:bg-slate-950 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-lg overflow-hidden">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
              <p className="text-sm text-slate-400">Cargando su cita...</p>
            </div>
          ) : !appointment ? (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center gap-3">
              <div className="w-14 h-14 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                <AlertTriangle className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-base font-semibold text-slate-700 dark:text-slate-200">
                Enlace no válido
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400">{error}</p>
            </div>
          ) : mode === "reschedule" ? (
            <>
              <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-100 dark:border-slate-800">
                <button
                  onClick={() => { setMode("view"); setError(null); }}
                  className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <p className="text-base font-semibold text-slate-900 dark:text-slate-100">
                  Elija un nuevo horario
                </p>
              </div>

              <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
                {error && (
                  <p className="mb-3 text-sm text-red-500 text-center">{error}</p>
                )}
                {loadingSlots ? (
                  <div className="flex justify-center py-10">
                    <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
                  </div>
                ) : !slotsData?.canReschedule ? (
                  <p className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                    Ya no es posible reprogramar esta cita desde aquí. Escríbanos por
                    WhatsApp y le ayudamos.
                  </p>
                ) : !slotsData?.hasAvailability ? (
                  <p className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                    No hay horarios publicados en este momento. Escríbanos por WhatsApp
                    y coordinamos una nueva fecha.
                  </p>
                ) : slotsData.days.length === 0 ? (
                  <p className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                    No quedan horarios libres en las próximas semanas. Escríbanos por
                    WhatsApp, por favor.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {slotsData.days.map((day) => (
                      <div key={day.date}>
                        <p className="mb-2 text-[11px] font-bold text-slate-400 uppercase tracking-wider capitalize">
                          {formatDayHeading(day.date, slotsData.timeZone)}
                        </p>
                        <div className="grid grid-cols-3 gap-2">
                          {day.slots.map((slot) => (
                            <button
                              key={slot.start}
                              onClick={() => pickSlot(slot.start)}
                              disabled={acting}
                              className="py-2 text-sm font-medium text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 rounded-lg hover:border-indigo-500 hover:text-indigo-600 disabled:opacity-40 transition-colors"
                            >
                              {slot.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="px-6 pt-7 pb-5 text-center border-b border-slate-100 dark:border-slate-800">
                <div className="w-12 h-12 rounded-full bg-indigo-50 dark:bg-indigo-950/40 flex items-center justify-center mx-auto mb-3">
                  <CalendarDays className="w-6 h-6 text-indigo-600" />
                </div>
                <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Hola{appointment.firstName ? `, ${appointment.firstName}` : ""}
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  {isOpen
                    ? "Confirme si podrá asistir a su cita"
                    : "Detalle de su cita"}
                </p>
              </div>

              <div className="px-6 py-5 space-y-3 bg-slate-50 dark:bg-slate-800/30">
                <Row icon={CalendarDays}>
                  <span className="capitalize font-medium text-slate-900 dark:text-slate-100">
                    {formatDate(appointment.scheduledAt, appointment.timeZone)}
                  </span>
                </Row>
                <Row icon={Clock}>
                  <span className="font-medium text-slate-900 dark:text-slate-100">
                    {formatTime(appointment.scheduledAt, appointment.timeZone)}
                  </span>
                  <span className="text-slate-400"> · {appointment.durationMinutes} min</span>
                </Row>
                {appointment.reason && <Row icon={CheckCircle2}>{appointment.reason}</Row>}
                {appointment.professional && (
                  <Row icon={UserRound}>{appointment.professional}</Row>
                )}
                {appointment.location && <Row icon={MapPin}>{appointment.location}</Row>}
              </div>

              {resolved ? (
                <>
                  <div className="px-6 py-7 text-center">
                    <div
                      className={`w-14 h-14 rounded-full ${resolved.ring} flex items-center justify-center mx-auto mb-3`}
                    >
                      <resolved.icon className={`w-7 h-7 ${resolved.tone}`} />
                    </div>
                    <p className="text-base font-semibold text-slate-900 dark:text-slate-100">
                      {resolved.title}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      {resolved.body}
                    </p>
                    {appointment.status === "confirmed" && !isPast && (
                      <button
                        onClick={openReschedule}
                        className="mt-4 text-sm font-semibold text-indigo-600 hover:underline"
                      >
                        Necesito cambiar el horario
                      </button>
                    )}
                  </div>
                  {calendarButtons}
                </>
              ) : isPast ? (
                <div className="px-6 py-7 text-center">
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Esta cita ya pasó. Si necesita agendar una nueva, escríbanos por WhatsApp.
                  </p>
                </div>
              ) : (
                <div className="px-6 py-5 space-y-3">
                  {error && (
                    <p className="text-sm text-red-500 text-center">{error}</p>
                  )}
                  <button
                    onClick={() => act("confirm")}
                    disabled={acting}
                    className="w-full flex items-center justify-center gap-2 py-3.5 text-base font-semibold bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 transition-colors shadow-sm"
                  >
                    {acting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-5 h-5" />
                    )}
                    Confirmar mi cita
                  </button>
                  <button
                    onClick={openReschedule}
                    disabled={acting}
                    className="w-full flex items-center justify-center gap-2 py-3.5 text-base font-semibold text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-900 rounded-xl hover:bg-indigo-50 dark:hover:bg-indigo-950/30 disabled:opacity-50 transition-colors"
                  >
                    <CalendarClock className="w-5 h-5" />
                    Reprogramar
                  </button>
                  <button
                    onClick={() => act("cancel")}
                    disabled={acting}
                    className="w-full flex items-center justify-center gap-2 py-3.5 text-base font-semibold text-red-600 dark:text-red-400 border border-red-200 dark:border-red-900 rounded-xl hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-50 transition-colors"
                  >
                    <XCircle className="w-5 h-5" />
                    Cancelar cita
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppointmentConfirm;
